import {
  findAccount,
  normalizeEmail,
  type AuthAccount,
  type AuthAttempt,
  type AuthState,
} from "./auth";
import type { Student } from "./data";

const TEMP_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export type KidDecision = "accept" | "decline";

export type ParentClaimInput = {
  given: string;
  family: string;
  phone: string;
  confirmed: boolean;
  password: string;
  confirmPassword: string;
  decisions: Record<string, KidDecision | undefined>;
};

export type ParentInvite = {
  email: string;
  given: string;
  family: string;
  phone: string;
  /** Empty when this parent already chose their own password. */
  password: string;
  studentNames: string[];
  pendingStudentIds: string[];
  created: boolean;
  mustResetPassword: boolean;
};

export type ClaimSkip = {
  email: string;
  reason: string;
};

/** Readable one-time password. It signs the parent in and cannot be kept. */
export function temporaryPassword(random: () => number = Math.random): string {
  let body = "";
  for (let i = 0; i < 8; i += 1) {
    const index = Math.floor(random() * TEMP_ALPHABET.length) % TEMP_ALPHABET.length;
    body += TEMP_ALPHABET[index] ?? "A";
  }
  return `Lift-${body}`;
}

export function splitGuardianName(
  name: string,
  fallbackFamily: string,
): { given: string; family: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const family = fallbackFamily.trim() || "Family";
  if (parts.length === 0) return { given: "Parent", family };
  if (parts.length === 1) return { given: parts[0] ?? "Parent", family };
  return { given: parts[0] ?? "Parent", family: parts.slice(1).join(" ") };
}

function passwordLengthError(password: string, minLength: number): string | undefined {
  if (password.trim().length < minLength) {
    return `Password needs at least ${minLength} characters.`;
  }
  return undefined;
}

export function validateParentClaim(
  account: AuthAccount,
  input: ParentClaimInput,
  minLength = 4,
): string | null {
  if (!input.confirmed) {
    return "Confirm that this name, email, and mobile number are yours before continuing.";
  }
  if (!input.given.trim() || !input.family.trim()) {
    return "Fill in your name.";
  }
  if (!input.phone.trim()) {
    return "Add a mobile number for guardian contact.";
  }
  const undecided = account.pendingStudentIds.some(
    (id) => input.decisions[id] !== "accept" && input.decisions[id] !== "decline",
  );
  if (undecided) {
    return "Say whether each student is your child before continuing.";
  }
  if (!account.mustResetPassword) return null;
  const nextPassword = input.password.trim();
  const lengthError = passwordLengthError(nextPassword, minLength);
  if (lengthError) return lengthError;
  if (nextPassword !== input.confirmPassword.trim()) {
    return "Passwords do not match.";
  }
  if (nextPassword === account.password) {
    return "That is the invitation password. Choose a different password to continue.";
  }
  return null;
}

export function finishParentClaim(
  state: AuthState,
  email: string,
  input: ParentClaimInput,
  minLength = 4,
): AuthAttempt {
  const account = findAccount(state, email);
  if (!account || account.role !== "parent") {
    return { ok: false, error: "No account for that email.", state };
  }
  const error = validateParentClaim(account, input, minLength);
  if (error) return { ok: false, error, state };
  const accepted = account.pendingStudentIds.filter(
    (id) => input.decisions[id] === "accept",
  );
  const next: AuthAccount = {
    ...account,
    given: input.given.trim(),
    family: input.family.trim(),
    phone: input.phone.trim(),
    password: account.mustResetPassword ? input.password.trim() : account.password,
    mustResetPassword: false,
    pendingStudentIds: [],
    studentIds: [...account.studentIds, ...accepted.filter((id) => !account.studentIds.includes(id))],
  };
  return {
    ok: true,
    state: {
      ...state,
      accounts: state.accounts.map((item) =>
        item.email === account.email ? next : item,
      ),
    },
  };
}

/**
 * One parent login per guardian email on the imported roster.
 * New accounts get a temporary password and cannot continue until they replace
 * it and accept each assigned child. An existing password is left alone.
 */
export function issueMigratedParents(
  state: AuthState,
  students: Student[],
  random: () => number = Math.random,
): { state: AuthState; invites: ParentInvite[]; skipped: ClaimSkip[] } {
  const groups = new Map<string, Student[]>();
  students.forEach((student) => {
    const email = normalizeEmail(student.guardianEmail);
    if (!email || !email.includes("@")) return;
    const list = groups.get(email) ?? [];
    list.push(student);
    groups.set(email, list);
  });

  let accounts = state.accounts.map((account) => ({ ...account }));
  const invites: ParentInvite[] = [];
  const skipped: ClaimSkip[] = [];

  groups.forEach((kids, email) => {
    const existing = accounts.find((account) => account.email === email);
    if (existing && existing.role !== "parent") {
      skipped.push({
        email,
        reason: "That email already belongs to a student or staff account.",
      });
      return;
    }
    const [first] = kids;
    const name = splitGuardianName(
      first?.guardianName ?? "",
      first?.family ?? "",
    );
    const phone =
      kids.find((kid) => kid.guardianPhone.trim())?.guardianPhone.trim() ??
      existing?.phone ??
      "";
    const already =
      new Set([
        ...(existing?.studentIds ?? []),
        ...(existing?.pendingStudentIds ?? []),
      ]);
    const assigned = kids.filter((kid) => !already.has(kid.id));
    if (!existing && assigned.length === 0) return;
    if (existing && assigned.length === 0 && !existing.mustResetPassword) return;

    const created = !existing;
    const mustResetPassword = existing ? existing.mustResetPassword : true;
    const password = created
      ? temporaryPassword(random)
      : existing?.password ?? "";
    const pendingStudentIds = [
      ...(existing?.pendingStudentIds ?? []),
      ...assigned.map((kid) => kid.id),
    ];
    const next: AuthAccount = {
      email,
      password,
      role: "parent",
      given: existing?.given || name.given,
      family: existing?.family || name.family,
      phone: existing?.phone || phone,
      studentId: null,
      studentIds: existing?.studentIds ?? [],
      staffId: null,
      mustResetPassword,
      pendingStudentIds,
    };
    accounts = existing
      ? accounts.map((account) => (account.email === email ? next : account))
      : [next, ...accounts];
    invites.push({
      email,
      given: next.given,
      family: next.family,
      phone: next.phone,
      password: created ? password : "",
      studentNames: assigned.map((kid) => `${kid.given} ${kid.family}`),
      pendingStudentIds,
      created,
      mustResetPassword,
    });
  });

  return { state: { ...state, accounts }, invites, skipped };
}
